import { Check, ChevronDown, Loader2, MessagesSquare, Pencil, RotateCcw, Share2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReasoningEffortPicker } from "@/components/chat/ReasoningEffortPicker";
import { roomReasoningConfig } from "@/components/chat/roomReasoningConfig";
import { AgentAvatar } from "@/components/group-chat/AgentAvatar";
import { GroupChatHistorySidebar } from "@/components/group-chat/GroupChatHistorySidebar";
import type { GroupChatSendPayload } from "@/components/group-chat/GroupChatInput";
import { GroupChatInput } from "@/components/group-chat/GroupChatInput";
import { GroupChatMessageList } from "@/components/group-chat/GroupChatMessageList";
import { EntityShareDialog, TagPopover } from "@/components/shared";
import { type ToastType, useToast } from "@/components/shared/Toast";
import { getRuntimeConfig } from "@/config/runtime";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useOperations } from "@/context/OperationContext";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { groupChatService } from "@/lib/groupChatService";
import { authService, googleDriveService, sharepointService } from "@/lib/services";
import type {
  GroupChatAgentMessageEvent,
  GroupChatAgentStartEvent,
  GroupChatMessage,
  GroupChatSession,
  SharePointDocumentMeta,
  SharePointItem,
  SpecialistAgent,
} from "@/types";

// ── Pure helpers for handleSendMessage ────────────────────────────────────

/** Build preliminary SharePoint metadata for optimistic UI display. */
function buildPreliminarySpMetas(items: SharePointItem[]): SharePointDocumentMeta[] {
  return items.map((f) => ({
    filename: f.name,
    url: f.web_url,
    size: f.size,
    mimeType: f.mime_type,
  }));
}

interface SharePointDownloadResult {
  metas: SharePointDocumentMeta[];
  contentText?: string;
  slideImages?: string[];
}

/** Download cloud documents (SharePoint or Google Drive) and build metadata + content text. */
async function downloadSharePointDocuments(items: SharePointItem[]): Promise<SharePointDownloadResult> {
  const isGoogle = getRuntimeConfig().authProvider === "google";
  const metas: SharePointDocumentMeta[] = [];
  const documentSections: string[] = [];
  const allSlideImages: string[] = [];

  for (const file of items) {
    try {
      const doc = isGoogle
        ? await googleDriveService.downloadDocument(file.id)
        : await sharepointService.downloadDocument(file.web_url);
      metas.push({
        filename: doc.filename,
        url: doc.source_url,
        size: doc.size,
        mimeType: doc.mime_type,
      });

      if (doc.slide_images && doc.slide_images.length > 0) {
        allSlideImages.push(...doc.slide_images);
        documentSections.push(
          `### ${doc.filename}\n*(Presentation with ${doc.slide_images.length} slides — images attached)*`,
        );
      } else {
        let docSection = `### ${doc.filename}\n`;
        if (doc.truncated) {
          docSection += `*(Document truncated — original size: ${doc.size} bytes)*\n`;
        }
        docSection += `\n${doc.content}`;
        documentSections.push(docSection);
      }
    } catch (err) {
      console.error(`Failed to download file ${file.name}:`, err);
      metas.push({ filename: file.name, url: file.web_url, size: file.size, mimeType: file.mime_type });
      documentSections.push(
        `### ${file.name}\n*Failed to download file content. Reference: [${file.name}](${file.web_url})*`,
      );
    }
  }

  return {
    metas: metas.length > 0 ? metas : [],
    contentText:
      documentSections.length > 0 ? `## Attached Documents\n\n${documentSections.join("\n\n---\n\n")}` : undefined,
    slideImages: allSlideImages.length > 0 ? allSlideImages : undefined,
  };
}

/** Upload files to group chat and return their UUIDs, or undefined on failure. */
async function uploadAttachments(
  sessionUuid: string,
  files: File[],
  label: string,
  showToast: (msg: string, level: ToastType) => void,
): Promise<string[] | undefined> {
  try {
    const uploadRes = await groupChatService.uploadAttachments(sessionUuid, files);
    return uploadRes.data.map((att) => att.uuid);
  } catch (err) {
    console.error(`[GroupChat] Failed to upload ${label} attachments:`, err);
    showToast(`Failed to upload ${label}. Sending message without them.`, "error");
    return undefined;
  }
}

export function GroupChat() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { trackOperation } = useOperations();
  const { profile, updatePreferences } = useAuth();
  const { enabledModels } = useApp();

  const [agents, setAgents] = useState<SpecialistAgent[]>([]);
  const [currentSession, setCurrentSession] = useState<GroupChatSession | null>(null);
  // Room-level reasoning effort chosen before the room exists (sent on create).
  const [pendingRoomEffort, setPendingRoomEffort] = useState<string | null>(null);
  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);
  const [pendingInput, setPendingInput] = useState("");
  const [thinkingAgent, setThinkingAgent] = useState<{
    name: string;
    icon?: string;
    color?: string;
  } | null>(null);
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  // Bench picker state
  const [benchOpen, setBenchOpen] = useState(false);
  const [benchAgentUuids, setBenchAgentUuids] = useState<string[] | null>(null);
  const benchRef = useRef<HTMLDivElement>(null);

  // Operation stream state for OperationManager-based SSE
  const [operationId, setOperationId] = useState<string | null>(null);
  // Ref to track temp user message UUID so the onEvent callback can replace it
  const tempUserUuidRef = useRef<string | null>(null);
  // Ref to track session UUID for post-stream reload
  const activeSessionUuidRef = useRef<string | null>(null);

  // Initialize bench from user preferences
  useEffect(() => {
    const saved = profile?.preferences?.groupChatBench;
    if (saved && saved.length > 0) {
      setBenchAgentUuids(saved);
    } else {
      setBenchAgentUuids(null); // null = all agents
    }
  }, [profile?.preferences?.groupChatBench]);

  // Close bench dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (benchRef.current && !benchRef.current.contains(e.target as Node)) {
        setBenchOpen(false);
      }
    };
    if (benchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [benchOpen]);

  // Load specialist agents on mount
  useEffect(() => {
    loadAgents();
  }, []);

  // Load session if sessionId is provided
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    } else {
      setCurrentSession(null);
      setMessages([]);
    }
  }, [sessionId]);

  const loadAgents = async () => {
    try {
      const data = await groupChatService.listSpecialistAgents();
      setAgents(data);
    } catch (err) {
      console.error("Failed to load agents:", err);
      showToast("Failed to load specialist personas", "error");
    }
  };

  const loadSession = async (id: string) => {
    try {
      setIsInitialLoading(true);
      const session = await groupChatService.getSession(id);
      setCurrentSession(session);
      setMessages(session.messages || []);
    } catch (err) {
      console.error("Failed to load session:", err);
      showToast("Failed to load discussion", "error");
      navigate("/group-chat");
    } finally {
      setIsInitialLoading(false);
    }
  };

  const refreshSidebar = useCallback(() => {
    setSidebarRefreshKey((prev) => prev + 1);
  }, []);

  // Persist bench selection to user settings
  const saveBench = useCallback(
    async (uuids: string[] | null) => {
      try {
        const benchValue = uuids && uuids.length > 0 ? uuids : [];
        await authService.updateSettings({ groupChatBench: benchValue });
        updatePreferences({ groupChatBench: benchValue.length > 0 ? benchValue : undefined });
      } catch (err) {
        console.error("Failed to save bench:", err);
        showToast("Failed to save persona selection", "error");
      }
    },
    [showToast, updatePreferences],
  );

  const toggleBenchAgent = useCallback(
    (agentUuid: string) => {
      setBenchAgentUuids((prev) => {
        let next: string[] | null;
        if (prev === null) {
          // Currently "all" — switching to custom: select all except this one
          next = agents.map((a) => a.uuid).filter((uuid) => uuid !== agentUuid);
        } else if (prev.includes(agentUuid)) {
          // Remove agent — but don't allow empty bench (minimum 2 agents)
          const filtered = prev.filter((uuid) => uuid !== agentUuid);
          if (filtered.length < 2) {
            showToast("At least 2 personas are required", "info");
            return prev;
          }
          next = filtered;
        } else {
          // Add agent
          next = [...prev, agentUuid];
          // If all agents are now selected, switch to "all" (null)
          if (next.length >= agents.length) {
            next = null;
          }
        }
        saveBench(next);
        return next;
      });
    },
    [agents, saveBench, showToast],
  );

  const resetBench = useCallback(() => {
    setBenchAgentUuids(null);
    saveBench(null);
  }, [saveBench]);

  const isAgentInBench = useCallback(
    (agentUuid: string) => {
      if (benchAgentUuids === null) return true; // all agents
      return benchAgentUuids.includes(agentUuid);
    },
    [benchAgentUuids],
  );

  const benchCount = benchAgentUuids === null ? agents.length : benchAgentUuids.length;

  const roomConfig = useMemo(
    () => roomReasoningConfig(agents, enabledModels, isAgentInBench),
    [agents, enabledModels, isAgentInBench],
  );
  const roomEffort = currentSession ? (currentSession.reasoningEffort ?? null) : pendingRoomEffort;

  const handleRoomEffortChange = (effort: string | null) => {
    if (!currentSession) {
      setPendingRoomEffort(effort);
      return;
    }
    void groupChatService
      .updateSession(currentSession.uuid, { reasoningEffort: effort })
      .then(() => setCurrentSession((prev) => (prev ? { ...prev, reasoningEffort: effort } : prev)))
      .catch((err) => {
        console.error("Failed to save reasoning effort:", err);
        showToast("Failed to save reasoning effort", "error");
      });
  };

  // ── Operation stream event handler ──────────────────────────────────────
  const handleGroupChatEvent = useCallback(
    // NOSONAR -- SSE event switch handler with per-event-type message construction
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "user_message": {
          // Replace temp user message with the real one from the server
          const realUuid = data.uuid as string;
          const tempUuid = tempUserUuidRef.current;
          if (tempUuid) {
            setMessages((prev) =>
              prev.map((m) =>
                m.uuid === tempUuid
                  ? { ...m, uuid: realUuid, createdAt: (data.createdAt as string) || m.createdAt }
                  : m,
              ),
            );
            tempUserUuidRef.current = null;
          }
          break;
        }

        case "orchestrator_message": {
          const orchMsg: GroupChatMessage = {
            uuid: `orch-${Date.now()}`,
            senderType: "orchestrator",
            content: data.content as string,
            createdAt: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, orchMsg]);
          break;
        }

        case "agent_start": {
          const agentData = data as unknown as GroupChatAgentStartEvent;
          setThinkingAgent({
            name: agentData.agentName,
            icon: agentData.agentIcon,
            color: agentData.agentColor,
          });
          break;
        }

        case "agent_message": {
          const msgData = data as unknown as GroupChatAgentMessageEvent;
          const agentMsg: GroupChatMessage = {
            uuid: msgData.uuid,
            senderType: "agent",
            senderId: msgData.senderId,
            senderName: msgData.senderName,
            content: msgData.content,
            tokensUsed: msgData.tokensUsed,
            messageMetadata: msgData.messageMetadata,
            createdAt: msgData.createdAt,
          };
          setMessages((prev) => [...prev, agentMsg]);
          setThinkingAgent(null);
          break;
        }

        case "synthesis": {
          const synthMsg: GroupChatMessage = {
            uuid: `synth-temp-${Date.now()}`,
            senderType: "orchestrator",
            senderName: "Synthesis",
            content: data.content as string,
            messageMetadata: { is_synthesis: true },
            createdAt: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, synthMsg]);
          break;
        }

        case "synthesis_saved": {
          const savedUuid = data.uuid as string;
          const savedCreatedAt = data.createdAt as string;
          setMessages((prev) =>
            prev.map((m) =>
              m.uuid.startsWith("synth-temp-")
                ? { ...m, uuid: savedUuid, createdAt: savedCreatedAt || m.createdAt }
                : m,
            ),
          );
          break;
        }

        case "discussion_end": {
          setThinkingAgent(null);
          break;
        }

        case "error": {
          const errMsg = (data.message as string) || "An error occurred";
          showToast(`Discussion error: ${errMsg}`, "error");
          setThinkingAgent(null);
          break;
        }

        case "operation_completed": {
          // Stream finished — reload session to get updated state
          setThinkingAgent(null);
          setIsLoading(false);
          setOperationId(null);
          const sessionUuid = activeSessionUuidRef.current;
          if (sessionUuid) {
            loadSession(sessionUuid);
            refreshSidebar();
          }
          break;
        }

        case "operation_failed": {
          const errorData = data as { error?: string };
          console.error("Group chat operation failed:", errorData.error);
          showToast("Discussion failed. Please try again.", "error");
          setThinkingAgent(null);
          setIsLoading(false);
          setOperationId(null);
          // Remove temp messages on failure
          setMessages((prev) => prev.filter((m) => !m.uuid.startsWith("temp-")));
          break;
        }
      }
    },
    [showToast, refreshSidebar],
  );

  useOperationStream(operationId, {
    onEvent: handleGroupChatEvent,
    onError: (err) => {
      console.error("Group chat operation stream error:", err);
      setThinkingAgent(null);
      setIsLoading(false);
      setOperationId(null);
      setMessages((prev) => prev.filter((m) => !m.uuid.startsWith("temp-")));
    },
  });

  const handleSendMessage = async (payload: GroupChatSendPayload) => {
    const { message: content, imageFiles, documentFiles, sharepointItems } = payload;
    if (!content.trim() || isLoading) return;

    try {
      setIsLoading(true);
      setThinkingAgent(null);

      // Create new session if needed
      let session = currentSession;
      if (!session) {
        session = await groupChatService.createSession({
          title: content.slice(0, 100),
          reasoningEffort: pendingRoomEffort ?? undefined,
        });
        setCurrentSession(session);
        navigate(`/group-chat/${session.uuid}`, { replace: true });
      }

      // Build preliminary SP metadata for optimistic UI
      const preliminaryMetas =
        sharepointItems && sharepointItems.length > 0 ? buildPreliminarySpMetas(sharepointItems) : undefined;

      // Add user message immediately to UI
      const userMessage: GroupChatMessage = {
        uuid: `temp-${Date.now()}`,
        senderType: "user",
        content,
        messageMetadata: preliminaryMetas ? { sharepoint_documents: preliminaryMetas } : undefined,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Download SharePoint documents (user message is already visible)
      let sharepointMetas: SharePointDocumentMeta[] | undefined;
      let sharepointContentText: string | undefined;
      let sharepointSlideImages: string[] | undefined;
      if (sharepointItems && sharepointItems.length > 0) {
        setIsDownloadingFiles(true);
        try {
          const spResult = await downloadSharePointDocuments(sharepointItems);
          if (spResult.metas.length > 0) {
            sharepointMetas = spResult.metas;
            setMessages((prev) =>
              prev.map((m) =>
                m.uuid === userMessage.uuid ? { ...m, messageMetadata: { sharepoint_documents: spResult.metas } } : m,
              ),
            );
          }
          sharepointContentText = spResult.contentText;
          sharepointSlideImages = spResult.slideImages;
        } finally {
          setIsDownloadingFiles(false);
        }
      }

      // Upload attachments
      const imageAttachmentUuids =
        imageFiles && imageFiles.length > 0
          ? await uploadAttachments(session.uuid, imageFiles, "images", showToast)
          : undefined;

      const documentAttachmentUuids =
        documentFiles && documentFiles.length > 0
          ? await uploadAttachments(session.uuid, documentFiles, "documents", showToast)
          : undefined;

      // Start streaming via OperationManager
      activeSessionUuidRef.current = session.uuid;
      tempUserUuidRef.current = userMessage.uuid;

      const result = await groupChatService.sendStreamMessage(session.uuid, {
        content,
        imageAttachments: imageAttachmentUuids,
        documentAttachments: documentAttachmentUuids,
        sharepointDocuments: sharepointMetas,
        sharepointContent: sharepointContentText,
        sharepointSlideImages,
      });
      setOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "group_chat",
        entity_id: session.uuid,
        status: "running",
        title: session.title || "Group chat",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });

      // Note: isLoading is cleared by the operation_completed/operation_failed event handler
    } catch (err) {
      console.error("[GroupChat] Failed to send message:", err);
      showToast("Failed to send message. Please try again.", "error");
      setMessages((prev) => prev.filter((m) => !m.uuid.startsWith("temp-")));
      setIsLoading(false);
      setThinkingAgent(null);
    }
  };

  const handleInterrupt = async () => {
    if (!currentSession || !isLoading) return;

    try {
      await groupChatService.interruptDiscussion(currentSession.uuid, {
        reason: "User interrupted",
      });
      setIsLoading(false);
      setThinkingAgent(null);
      await loadSession(currentSession.uuid);
      showToast("Discussion interrupted", "info");
    } catch (err) {
      console.error("Failed to interrupt discussion:", err);
      showToast("Failed to interrupt discussion", "error");
    }
  };

  const handleSynthesize = async () => {
    if (!currentSession || isLoading) return;

    try {
      setIsLoading(true);
      const synthMsg = await groupChatService.synthesize(currentSession.uuid);
      setMessages((prev) => [...prev, synthMsg]);
      showToast("Synthesis generated", "success");
    } catch (err) {
      console.error("Failed to generate synthesis:", err);
      const message = err instanceof Error ? err.message : "Failed to generate synthesis";
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const startNewDiscussion = () => {
    setCurrentSession(null);
    setPendingRoomEffort(null);
    setMessages([]);
    setPendingInput("");
    navigate("/group-chat");
  };

  const handleQuickAction = useCallback((actionText: string) => {
    setPendingInput(actionText);
  }, []);

  const handleSaveTitle = async () => {
    if (!currentSession || !editTitle.trim()) return;

    try {
      await groupChatService.updateSession(currentSession.uuid, {
        title: editTitle.trim(),
      });
      setCurrentSession((prev) => (prev ? { ...prev, title: editTitle.trim() } : prev));
      setIsEditingTitle(false);
      refreshSidebar();
      showToast("Title updated", "success");
    } catch (err) {
      console.error("Failed to update title:", err);
      showToast("Failed to update title", "error");
    }
  };

  const handleStartEditTitle = () => {
    setEditTitle(currentSession?.title || "");
    setIsEditingTitle(true);
  };

  // Get active participants
  const activeParticipants = currentSession?.participants?.filter((p) => p.isActive) || [];

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex overflow-hidden">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        {/* Header */}
        <div className="border-b border-border-primary p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 shrink">
              <div className="w-7 h-7 rounded-lg bg-brand-gradient-br flex items-center justify-center shrink-0">
                <MessagesSquare className="w-4 h-4 text-white" />
              </div>
              {currentSession && isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveTitle();
                      if (e.key === "Escape") setIsEditingTitle(false);
                    }}
                    className="bg-surface-secondary text-content-primary rounded-sm px-2 py-1 text-sm font-semibold focus:outline-hidden focus:ring-2 focus:ring-brand-primary w-64"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveTitle}
                    className="p-1 hover:bg-interactive-active rounded-sm text-green-400"
                    title="Save title"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsEditingTitle(false)}
                    className="p-1 hover:bg-interactive-active rounded-sm text-content-tertiary"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group min-w-0">
                  <h1 className="text-content-primary font-semibold truncate">
                    {currentSession?.title || "Brainstorm"}
                  </h1>
                  {currentSession && (
                    <button
                      onClick={handleStartEditTitle}
                      className="p-1 hover:bg-interactive-active rounded-sm text-content-quaternary opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      title="Edit title"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Bench Picker */}
            {agents.length > 0 && (
              <div className="relative shrink-0" ref={benchRef}>
                <button
                  onClick={() => setBenchOpen((prev) => !prev)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition text-xs font-medium border ${
                    benchAgentUuids !== null
                      ? "bg-brand-primary-subtle border-brand-primary/30 text-brand-primary hover:bg-brand-primary-subtle"
                      : "bg-surface-secondary border-border-secondary text-content-secondary hover:bg-interactive-active"
                  }`}
                  title="Choose which personas can participate"
                >
                  <div className="flex items-center -space-x-1.5">
                    {agents
                      .filter((a) => isAgentInBench(a.uuid))
                      .slice(0, 4)
                      .map((a) => (
                        <AgentAvatar key={a.uuid} agent={a} size="sm" />
                      ))}
                    {benchCount > 4 && (
                      <span className="w-6 h-6 rounded-lg bg-surface-tertiary flex items-center justify-center text-[10px] text-content-primary font-medium">
                        +{benchCount - 4}
                      </span>
                    )}
                  </div>
                  <span>
                    {(() => {
                      if (benchAgentUuids === null) return "All personas";
                      return `${benchCount} persona${benchCount !== 1 ? "s" : ""}`;
                    })()}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${benchOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown */}
                {benchOpen && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-surface-secondary border border-border-secondary rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="px-3 py-2.5 border-b border-border-secondary flex items-center justify-between">
                      <span className="text-xs font-semibold text-content-secondary uppercase tracking-wider">
                        Persona Bench
                      </span>
                      {benchAgentUuids !== null && (
                        <button
                          onClick={resetBench}
                          className="flex items-center gap-1 text-xs text-content-tertiary hover:text-content-primary transition"
                          title="Reset to all personas"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Reset
                        </button>
                      )}
                    </div>
                    <div className="p-1.5 max-h-80 overflow-y-auto">
                      {agents.map((agent) => {
                        const inBench = isAgentInBench(agent.uuid);
                        return (
                          <button
                            key={agent.uuid}
                            onClick={() => toggleBenchAgent(agent.uuid)}
                            className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg transition ${
                              inBench
                                ? "bg-surface-tertiary/50 hover:bg-interactive-active"
                                : "hover:bg-interactive-active/30 opacity-50"
                            }`}
                          >
                            <AgentAvatar agent={agent} size="sm" />
                            <div className="flex-1 text-left min-w-0">
                              <div className="text-sm font-medium text-content-primary truncate">{agent.name}</div>
                              <div className="text-xs text-content-tertiary truncate">{agent.role}</div>
                            </div>
                            <div
                              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition ${
                                inBench ? "bg-brand-primary border-brand-primary" : "border-border-hover"
                              }`}
                            >
                              {inBench && <Check className="w-3 h-3 text-content-primary" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="px-3 py-2 border-t border-border-secondary">
                      <p className="text-[11px] text-content-quaternary">
                        The orchestrator picks from your bench each turn. Minimum 2 personas.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <ReasoningEffortPicker config={roomConfig} value={roomEffort} onChange={handleRoomEffortChange} />

            {/* Active Agents */}
            {activeParticipants.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-secondary rounded-lg shrink-0">
                <span className="text-xs text-content-tertiary">Active:</span>
                <div className="flex items-center -space-x-2">
                  {activeParticipants.map((p) => (
                    <AgentAvatar key={p.agent.uuid} agent={p.agent} size="sm" />
                  ))}
                </div>
                <span className="text-xs text-content-tertiary">
                  {activeParticipants.length} {activeParticipants.length === 1 ? "persona" : "personas"}
                </span>
              </div>
            )}

            {currentSession?.isActive && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-lg shrink-0">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs text-green-400 font-medium">Discussion in progress</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {sessionId && currentSession && (
              <button
                onClick={() => setIsShareDialogOpen(true)}
                className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition border border-border-secondary shrink-0 whitespace-nowrap flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            )}
            {sessionId && currentSession && (
              <TagPopover entityType="group_chat" entityId={currentSession.uuid} mode="compact" />
            )}
            <button
              onClick={startNewDiscussion}
              className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition border border-border-secondary shrink-0 whitespace-nowrap"
            >
              New Discussion
            </button>
          </div>
        </div>

        {/* Messages */}
        <GroupChatMessageList
          messages={messages}
          agents={agents}
          isLoading={isLoading}
          thinkingAgent={thinkingAgent}
          onQuickAction={handleQuickAction}
          onExamplePrompt={(content: string) => handleSendMessage({ message: content })}
        />

        {/* Input */}
        <GroupChatInput
          onSendMessage={handleSendMessage}
          onInterrupt={handleInterrupt}
          onContinueDiscussion={
            currentSession && messages.length > 0 && !isLoading
              ? () => handleSendMessage({ message: "Continue the discussion, go deeper on the key points" })
              : undefined
          }
          onSynthesize={
            currentSession && messages.filter((m) => m.senderType === "agent").length >= 2 && !isLoading
              ? handleSynthesize
              : undefined
          }
          isLoading={isLoading}
          isActive={currentSession?.isActive || false}
          isDownloadingFiles={isDownloadingFiles}
          pendingInput={pendingInput}
          onPendingInputConsumed={() => setPendingInput("")}
          agents={agents}
        />
      </div>

      {/* History Sidebar */}
      <GroupChatHistorySidebar
        currentSessionId={currentSession?.uuid}
        refreshKey={sidebarRefreshKey}
        onSessionDeleted={(uuid) => {
          if (currentSession?.uuid === uuid) {
            startNewDiscussion();
          }
        }}
      />

      {/* Share Dialog */}
      {currentSession && (
        <EntityShareDialog
          entityId={currentSession.uuid}
          entityTitle={currentSession.title || "Untitled Discussion"}
          isOpen={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
          entityLabel="Brainstorm"
          entityType="group_chat"
          shareLinkPath="/group-chat/shared/"
          service={groupChatService}
        />
      )}
    </div>
  );
}
